<template>
  <div class="usage-guide">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="el-icon-document"></i>
        邮箱配置使用说明
      </h1>
      <p class="page-subtitle">详细配置指南，助您快速上手</p>
    </div>

    <!-- 概述卡片 -->
    <div class="overview-card">
      <div class="overview-content">
        <div class="overview-icon">
          <i class="el-icon-info"></i>
        </div>
        <div class="overview-text">
          <h3>支持邮箱类型</h3>
          <p>本系统支持：<span class="highlight">Outlook（OAuth 2.0）</span>、<span class="highlight">QQ（SMTP）</span>、<span class="highlight">163/126（SMTP）</span>、<span class="highlight">Gmail（SMTP）</span>、<span class="highlight">Yahoo（SMTP）</span></p>
          <div class="principle">
            <strong>重要原则：</strong>绝大多数邮箱需开启两步验证并使用"授权码/应用专用密码"；Outlook 使用 OAuth 2.0 授权登录，不填写密码。
          </div>
        </div>
      </div>
    </div>

    <!-- 邮箱配置指南 -->
    <div class="guide-sections">
      <!-- Outlook 配置 -->
      <div class="guide-section outlook-section">
        <div class="section-header">
          <div class="section-icon outlook-icon">
            <i class="el-icon-message-solid"></i>
          </div>
          <div class="section-title">
            <h2>Outlook（OAuth 2.0｜Microsoft 365/Outlook.com）</h2>
            <span class="section-badge">推荐</span>
          </div>
        </div>
        
        <div class="section-content">
          <div class="description-box">
            <h4><i class="el-icon-lightbulb"></i> 核心原理</h4>
            <p>通过微软账号登录并同意权限，系统使用 Access Token 发送邮件。</p>
            <p><strong>适用：</strong>Outlook.com/Hotmail/Live 个人邮箱、Microsoft 365 工作/学校邮箱。</p>
          </div>

          <div class="steps-container">
            <h4><i class="el-icon-guide"></i> 操作步骤</h4>
            <div class="steps">
              <div class="step-item" v-for="(step, index) in outlookSteps" :key="index">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-content">
                  <p>{{ step }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="notes-box">
            <h4><i class="el-icon-warning"></i> 注意事项</h4>
            <ul>
              <li><strong>代表共享邮箱/公共邮箱发送：</strong>管理员需在 Exchange 中为您的账号授予"以其身份发送/代表发送"或"代表发送"。</li>
              <li><strong>频繁要求重新登录：</strong>请允许微软登录域名的 Cookie；如为公司设备，可能受安全策略影响，请联系 IT。</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- QQ 邮箱配置 -->
      <div class="guide-section qq-section">
        <div class="section-header">
          <div class="section-icon qq-icon">
            <i class="el-icon-chat-dot-square"></i>
          </div>
          <div class="section-title">
            <h2>QQ 邮箱（SMTP）</h2>
          </div>
        </div>
        
        <div class="section-content">
          <div class="steps-container">
            <h4><i class="el-icon-guide"></i> 配置步骤</h4>
            <div class="steps">
              <div class="step-item" v-for="(step, index) in qqSteps" :key="index">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-content">
                  <p>{{ step }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="config-table">
            <h4><i class="el-icon-setting"></i> 系统配置参数</h4>
            <el-table :data="qqConfig" stripe>
              <el-table-column prop="field" label="配置项" width="150"></el-table-column>
              <el-table-column prop="value" label="配置值"></el-table-column>
            </el-table>
          </div>

          <div class="notes-box">
            <h4><i class="el-icon-warning"></i> 常见问题</h4>
            <ul>
              <li><strong>认证失败：</strong>多因未使用授权码或端口/加密方式不匹配。</li>
              <li><strong>连接失败：</strong>检查防火墙/网络是否限制 465/587/993 端口。</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 163/126 邮箱配置 -->
      <div class="guide-section netease-section">
        <div class="section-header">
          <div class="section-icon netease-icon">
            <i class="el-icon-message"></i>
          </div>
          <div class="section-title">
            <h2>163/126 邮箱（SMTP）</h2>
          </div>
        </div>
        
        <div class="section-content">
          <div class="steps-container">
            <h4><i class="el-icon-guide"></i> 配置步骤</h4>
            <div class="steps">
              <div class="step-item" v-for="(step, index) in neteaseSteps" :key="index">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-content">
                  <p>{{ step }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="config-table">
            <h4><i class="el-icon-setting"></i> 系统配置参数</h4>
            <el-table :data="neteaseConfig" stripe>
              <el-table-column prop="field" label="配置项" width="150"></el-table-column>
              <el-table-column prop="value" label="配置值"></el-table-column>
            </el-table>
          </div>
        </div>
      </div>

      <!-- Gmail 配置 -->
      <div class="guide-section gmail-section">
        <div class="section-header">
          <div class="section-icon gmail-icon">
            <i class="el-icon-message-solid"></i>
          </div>
          <div class="section-title">
            <h2>Gmail（SMTP）</h2>
          </div>
        </div>
        
        <div class="section-content">
          <div class="description-box">
            <h4><i class="el-icon-lightbulb"></i> 前置条件</h4>
            <p>需开启"两步验证"。</p>
          </div>

          <div class="steps-container">
            <h4><i class="el-icon-guide"></i> 配置步骤</h4>
            <div class="steps">
              <div class="step-item" v-for="(step, index) in gmailSteps" :key="index">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-content">
                  <p>{{ step }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="config-table">
            <h4><i class="el-icon-setting"></i> 系统配置参数</h4>
            <el-table :data="gmailConfig" stripe>
              <el-table-column prop="field" label="配置项" width="150"></el-table-column>
              <el-table-column prop="value" label="配置值"></el-table-column>
            </el-table>
          </div>

          <div class="notes-box">
            <h4><i class="el-icon-info"></i> 提示</h4>
            <p>若看不到"应用专用密码"，通常为未启用两步验证或被组织策略禁用。</p>
          </div>
        </div>
      </div>

      <!-- Yahoo 配置 -->
      <div class="guide-section yahoo-section">
        <div class="section-header">
          <div class="section-icon yahoo-icon">
            <i class="el-icon-message"></i>
          </div>
          <div class="section-title">
            <h2>Yahoo 邮箱（SMTP）</h2>
          </div>
        </div>
        
        <div class="section-content">
          <div class="steps-container">
            <h4><i class="el-icon-guide"></i> 配置步骤</h4>
            <div class="steps">
              <div class="step-item" v-for="(step, index) in yahooSteps" :key="index">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-content">
                  <p>{{ step }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="config-table">
            <h4><i class="el-icon-setting"></i> 系统配置参数</h4>
            <el-table :data="yahooConfig" stripe>
              <el-table-column prop="field" label="配置项" width="150"></el-table-column>
              <el-table-column prop="value" label="配置值"></el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部提示 -->
    <div class="footer-tip">
      <div class="tip-content">
        <i class="el-icon-success"></i>
        <span>配置完成后，您就可以开始使用邮件群发功能了！如有问题，请联系技术支持。</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UsageGuide',
  data() {
    return {
      outlookSteps: [
        '在系统内点击"使用 Outlook 登录（OAuth 2.0）"',
        '跳转至微软登录页 → 使用您的邮箱登录',
        '在权限页选择"同意/允许"；如提示"需要管理员同意"，请联系组织管理员',
        '返回系统显示"授权成功"后即可发送邮件；令牌过期会自动续期或提示重新登录'
      ],
      qqSteps: [
        '登录网页版 QQ 邮箱 → 设置 → 账户 → 开启"POP3/IMAP/SMTP"',
        '按页面指引完成短信验证，获取"授权码"',
        '在系统中填写邮箱账号和授权码（非登录密码）'
      ],
      neteaseSteps: [
        '登录网页版邮箱 → 设置 → POP3/SMTP/IMAP → 开启 IMAP 与 SMTP',
        '按指引生成"客户端授权密码/授权码"',
        '在系统中填写邮箱账号和授权密码'
      ],
      gmailSteps: [
        '进入 Google 账户安全 → 启用"两步验证"',
        '打开"应用专用密码" → 选择"邮件"并生成密码',
        '在系统中填写邮箱账号和应用专用密码'
      ],
      yahooSteps: [
        '进入 Yahoo 账户安全 → 开启两步验证',
        '生成"应用密码"并妥善保存',
        '在系统中填写邮箱账号和应用密码'
      ],
      qqConfig: [
        { field: '邮箱账号', value: '完整 QQ 邮箱' },
        { field: '密码', value: '授权码（非登录密码）' },
        { field: 'IMAP', value: 'imap.qq.com 端口 993 SSL/TLS' },
        { field: 'SMTP', value: 'smtp.qq.com 端口 465 SSL/TLS 或 587 STARTTLS' }
      ],
      neteaseConfig: [
        { field: '邮箱账号', value: '完整邮箱地址' },
        { field: '密码', value: '授权密码' },
        { field: 'IMAP', value: 'imap.163.com / imap.126.com 端口 993 SSL/TLS' },
        { field: 'SMTP', value: 'smtp.163.com / smtp.126.com 端口 465 SSL/TLS 或 587 STARTTLS' }
      ],
      gmailConfig: [
        { field: '邮箱账号', value: '完整 Gmail 地址' },
        { field: '密码', value: '应用专用密码' },
        { field: 'IMAP', value: 'imap.gmail.com 端口 993 SSL/TLS' },
        { field: 'SMTP', value: 'smtp.gmail.com 端口 587 STARTTLS' }
      ],
      yahooConfig: [
        { field: '邮箱账号', value: '完整 Yahoo 地址' },
        { field: '密码', value: '应用密码' },
        { field: 'IMAP', value: 'imap.mail.yahoo.com 端口 993 SSL/TLS' },
        { field: 'SMTP', value: 'smtp.mail.yahoo.com 端口 465 SSL/TLS 或 587 STARTTLS' }
      ]
    }
  }
}
</script>

<style scoped>
.usage-guide {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

/* 页面标题 */
.page-header {
  text-align: center;
  margin-bottom: 40px;
  animation: fadeInDown 1s ease-out;
}

.page-title {
  font-size: 2.5rem;
  color: #fff;
  margin-bottom: 10px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  font-weight: 700;
}

.page-title i {
  margin-right: 15px;
  color: #ffd700;
  animation: pulse 2s infinite;
}

.page-subtitle {
  font-size: 1.2rem;
  color: rgba(255,255,255,0.9);
  margin: 0;
}

/* 概述卡片 */
.overview-card {
  background: rgba(255,255,255,0.95);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  animation: slideInUp 1s ease-out;
}

.overview-content {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.overview-icon {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.overview-text h3 {
  color: #333;
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.overview-text p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
}

.highlight {
  background: linear-gradient(45deg, #ff6b6b, #feca57);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.principle {
  background: linear-gradient(45deg, #ff9a9e, #fecfef);
  padding: 15px;
  border-radius: 10px;
  border-left: 4px solid #ff6b6b;
}

/* 指南区块 */
.guide-sections {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.guide-section {
  background: rgba(255,255,255,0.95);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  animation: slideInUp 1s ease-out;
  transition: transform 0.3s ease;
}

.guide-section:hover {
  transform: translateY(-5px);
}

.section-header {
  display: flex;
  align-items: center;
  padding: 25px 30px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
}

.section-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-right: 20px;
  background: rgba(255,255,255,0.2);
}

.outlook-icon { background: linear-gradient(45deg, #0078d4, #106ebe); }
.qq-icon { background: linear-gradient(45deg, #12b7f5, #0ea5e9); }
.netease-icon { background: linear-gradient(45deg, #dd4b39, #ea4335); }
.gmail-icon { background: linear-gradient(45deg, #ea4335, #fbbc04); }
.yahoo-icon { background: linear-gradient(45deg, #6001d2, #7c3aed); }

.section-title {
  flex: 1;
}

.section-title h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.section-badge {
  background: #ffd700;
  color: #333;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 15px;
}

.section-content {
  padding: 30px;
}

.description-box, .steps-container, .config-table, .notes-box {
  margin-bottom: 25px;
}

.description-box h4, .steps-container h4, .config-table h4, .notes-box h4 {
  color: #333;
  margin-bottom: 15px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.description-box h4 i {
  color: #667eea;
}

.steps-container h4 i {
  color: #28a745;
}

.config-table h4 i {
  color: #17a2b8;
}

.notes-box h4 i {
  color: #ffc107;
}

.description-box p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 10px;
}

/* 步骤样式 */
.steps {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px;
  background: linear-gradient(45deg, #f8f9fa, #e9ecef);
  border-radius: 10px;
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;
}

.step-item:hover {
  background: linear-gradient(45deg, #e3f2fd, #bbdefb);
  transform: translateX(5px);
}

.step-number {
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
}

.step-content p {
  margin: 0;
  color: #555;
  line-height: 1.5;
}

/* 配置表格 */
.config-table {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
}

/* 注意事项 */
.notes-box {
  background: linear-gradient(45deg, #fff3cd, #ffeaa7);
  border-radius: 10px;
  padding: 20px;
  border-left: 4px solid #ffc107;
}

.notes-box ul {
  margin: 0;
  padding-left: 20px;
}

.notes-box li {
  color: #856404;
  line-height: 1.6;
  margin-bottom: 8px;
}

/* 底部提示 */
.footer-tip {
  text-align: center;
  margin-top: 40px;
  animation: fadeInUp 1s ease-out;
}

.tip-content {
  background: rgba(255,255,255,0.95);
  padding: 20px;
  border-radius: 15px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.tip-content i {
  color: #28a745;
  font-size: 20px;
}

.tip-content span {
  color: #333;
  font-weight: 500;
}

/* 动画效果 */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .usage-guide {
    padding: 15px;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .overview-content {
    flex-direction: column;
    text-align: center;
  }
  
  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .section-title h2 {
    font-size: 1.3rem;
  }
  
  .step-item {
    flex-direction: column;
    text-align: center;
  }
  
  .tip-content {
    flex-direction: column;
    text-align: center;
  }
}
</style>
